/**
 * Which state each trigger is in, for the Workflow2 redesign's Triggers tab (2026-10-10).
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * Plan: src/pages/Workflow2/PROJECT_NOTES.md (Workflow UI overhaul, step P1). The new builder reads the classic
 * builder's own `triggers` array (src/pages/Workflow2/components/WorkflowBuilder.vue) and this file
 * says what to draw for each entry. One state per trigger, first match wins:
 *
 *   current    it is the trigger on the canvas right now (even a disabled one: an old saved workflow
 *              must show its own trigger, not list it under "Coming soon")
 *   soon       the classic list has it disabled (`enabled` is not true)
 *   used      ANOTHER workflow already runs on it (each trigger runs one workflow)
 *   setup      Mango Voice without a MANGO_VOICE_SECURITY_TOKEN: the webhook would 403 every call
 *   inert      would be free, but the canvas already has a trigger (a workflow has one)
 *   available  free to place
 *
 * `used` compares the saved `TriggerOn` against the block type the classic builder writes
 * (TRIGGER_BLOCK_TYPES, the trigger half of mapTypeToBlockType), and skips the workflow being edited,
 * so a workflow never sees its own trigger as taken. The test pins TRIGGER_BLOCK_TYPES to the classic
 * map entry-for-entry (cypress/node-tests/workflow-trigger-states.test.js).
 *
 * Pure, and never throws on null / undefined / garbage inputs.
 */

export const TRIGGER_BLOCK_TYPES = {
  newpatient: 'NEW_PATIENT',
  appointmentscheduled: 'APPOINTMENT_SCHEDULED',
  appointmentbroken: 'APPOINTMENT_BROKEN',
  appointmentcompleted: 'APPOINTMENT_COMPLETED',
  procaddedquickadd: 'PROCEDURE_ADDED_VIA_PROCS_QUICK_ADD',
  preauthreceived: 'PRE_AUTH_RECEIVED',
  xfollowups: 'X_FOLLOWUPS',
  proceduremarkedreferral: 'PROCEDURE_MARKED_REFERRAL',
  websiteform: 'WEBSITE_FORM',
  facebooklead: 'FACEBOOK_LEAD',
  googlelead: 'GOOGLE_LEAD',
  mangovoice: 'MANGO_VOICE'
};

// Same fallback as the classic mapTypeToBlockType: an unmapped type is saved upper-cased.
export function triggerBlockType(type) {
  if (typeof type !== 'string' || type === '') return '';
  return Object.prototype.hasOwnProperty.call(TRIGGER_BLOCK_TYPES, type)
    ? TRIGGER_BLOCK_TYPES[type]
    : type.toUpperCase();
}

function isObject(v) {
  return v !== null && typeof v === 'object';
}

function idText(v) {
  return v === null || v === undefined ? '' : String(v);
}

// The workflow (other than the one being edited) that already runs on this block type, or null.
function findUsedBy(blockType, workflows, currentWorkflowID) {
  const excluded = currentWorkflowID === null || currentWorkflowID === undefined ? null : String(currentWorkflowID);
  for (let i = 0; i < workflows.length; i++) {
    const w = workflows[i];
    if (!isObject(w) || w.TriggerOn !== blockType) continue;
    if (excluded !== null && idText(w.WorkflowID) === excluded) continue;
    return { WorkflowID: w.WorkflowID, Name: w.Name || 'Unnamed' };
  }
  return null;
}

/**
 * @param {Object} args
 * @param {Array}  args.triggers            the classic `triggers` array ({type, label, description, enabled}), order kept
 * @param {Array}  args.existingWorkflows   the site's saved workflows ({WorkflowID, Name, TriggerOn})
 * @param {*}      args.currentWorkflowID   the workflow being edited (null / undefined on a new one: excludes nothing)
 * @param {string} args.placedTriggerType   the builder type of the trigger on the canvas, if any
 * @param {*}      args.mangoVoiceEnabled   the site has a Mango Voice security token
 * @returns {{ items: Array, allUsed: boolean, usedWorkflows: Array }}
 *   items          [{ type, label, description, state, usedBy }] — usedBy is { WorkflowID, Name } for 'used', else null
 *   allUsed        nothing is placed, nothing is 'available', and at least one trigger is 'used' (the
 *                  "every trigger is taken" card; an empty or all-disabled list is not "all used")
 *   usedWorkflows  the distinct workflows behind the 'used' items: [{ WorkflowID, Name, triggerType, triggerLabel }]
 */
export function triggerStates(args) {
  const a = isObject(args) ? args : {};
  const triggers = Array.isArray(a.triggers) ? a.triggers : [];
  const workflows = Array.isArray(a.existingWorkflows) ? a.existingWorkflows : [];
  const placed = typeof a.placedTriggerType === 'string' && a.placedTriggerType !== '' ? a.placedTriggerType : null;

  const items = [];
  triggers.forEach((t) => {
    if (!isObject(t) || typeof t.type !== 'string' || t.type === '') return;
    let state;
    let usedBy = null;
    if (t.type === placed) {
      state = 'current';
    } else if (t.enabled !== true) {
      state = 'soon';
    } else {
      usedBy = findUsedBy(triggerBlockType(t.type), workflows, a.currentWorkflowID);
      if (usedBy) state = 'used';
      else if (t.type === 'mangovoice' && !a.mangoVoiceEnabled) state = 'setup';
      else if (placed) state = 'inert';
      else state = 'available';
    }
    items.push({
      type: t.type,
      label: typeof t.label === 'string' ? t.label : t.type,
      description: typeof t.description === 'string' ? t.description : '',
      state,
      usedBy
    });
  });

  const usedWorkflows = [];
  const seen = {};
  items.forEach((i) => {
    if (i.state !== 'used') return;
    const key = i.usedBy.WorkflowID === null || i.usedBy.WorkflowID === undefined
      ? 'name:' + i.usedBy.Name
      : 'id:' + String(i.usedBy.WorkflowID);
    if (seen[key]) return;
    seen[key] = true;
    usedWorkflows.push({ WorkflowID: i.usedBy.WorkflowID, Name: i.usedBy.Name, triggerType: i.type, triggerLabel: i.label });
  });

  const allUsed = !placed && usedWorkflows.length > 0 && !items.some(i => i.state === 'available');

  return { items, allUsed, usedWorkflows };
}
